import { format } from "date-fns";

/**
 * Date and period utilities for YA grouping
 */

/**
 * Calculate the financial year for a given date based on year-end month
 *
 * Financial Year runs from (FY end month + 1) to (FY end month) of the following calendar year
 * Example: FY ending August 2027 = September 2026 to August 2027
 *
 * @param {Date} date - The date to check
 * @param {number} fyEndMonth - Financial year-end month (1-12)
 * @returns {number} Financial year (the year in which the FY ends)
 */
export function calculateFinancialYear(date, fyEndMonth) {
  const dateYear = date.getFullYear();
  const dateMonth = date.getMonth() + 1; // JavaScript months are 0-indexed

  // Financial Year runs from (fyEndMonth + 1) to (fyEndMonth) of the following calendar year
  // Example: FY ending August 2027 = September 2026 to August 2027
  //
  // If date is in months (fyEndMonth + 1) to 12, it's in the FY that ends NEXT calendar year
  // If date is in months 1 to fyEndMonth, it's in the FY that ends THIS calendar year

  if (dateMonth > fyEndMonth) {
    // Date is after FY end month (e.g., Sep-Dec for Aug year-end)
    // Example: Sep 2026 (month 9) with Aug year-end → FY 2027 (ends Aug 2027)
    // This is in the FY that ends in the NEXT calendar year
    return dateYear + 1;
  } else {
    // Date is on or before FY end month (e.g., Jan-Aug for Aug year-end)
    // Example: Jan 2027 (month 1) with Aug year-end → FY 2027 (ends Aug 2027)
    // This is in the FY that ends in the CURRENT calendar year
    return dateYear;
  }
}

/**
 * Calculate Year of Assessment (YA) from financial year
 * YA = FY year + 1
 *
 * @param {number} financialYear - Financial year
 * @returns {number} Year of Assessment
 */
export function calculateYA(financialYear) {
  return financialYear + 1;
}

/**
 * Get the financial year and YA for a given date
 *
 * @param {Date} date - The date to check
 * @param {number} fyEndMonth - Financial year-end month (1-12)
 * @returns {Object} { financialYear: number, ya: number }
 */
export function getFinancialYearAndYA(date, fyEndMonth) {
  const financialYear = calculateFinancialYear(date, fyEndMonth);
  const ya = calculateYA(financialYear);
  return { financialYear, ya };
}

/**
 * Group principal payments by Year of Assessment
 *
 * @param {Array} schedule - HP repayment schedule
 * @param {number} fyEndMonth - Financial year-end month (1-12)
 * @param {boolean} inAdvance - True if payments are made "In Advance"
 * @returns {Array} Array of { ya: number, principalPaid: number, rows: Array }
 */
export function groupByYA(schedule, fyEndMonth, inAdvance = false) {
  const yaGroups = {};

  schedule.forEach((row) => {
    let dateForFY = new Date(row.dueDate);
    let financialYear, ya;

    // Special handling for deposit (instalment 0) when payments are "In Arrears"
    // If deposit falls in the FY end month, it should be assigned to the next FY
    // This ensures deposits are grouped with the first instalment for tax purposes
    if (row.instalmentNo === 0 && !inAdvance) {
      const depositMonth = dateForFY.getMonth() + 1;
      const depositYear = dateForFY.getFullYear();

      // If deposit is in the FY end month, assign it to the next FY
      // if (depositMonth === fyEndMonth) {
      //   financialYear = depositYear + 1;
      //   ya = calculateYA(financialYear);
      // } else {
      // Otherwise, use normal calculation
      const result = getFinancialYearAndYA(dateForFY, fyEndMonth);
      financialYear = result.financialYear;
      ya = result.ya;
      // }
    } else {
      // For all other payments, use normal financial year calculation
      const result = getFinancialYearAndYA(dateForFY, fyEndMonth);
      financialYear = result.financialYear;
      ya = result.ya;
    }

    if (!yaGroups[ya]) {
      yaGroups[ya] = {
        ya,
        principalPaid: 0,
        rows: [],
      };
    }

    yaGroups[ya].principalPaid += row.principal;
    yaGroups[ya].rows.push(row);
  });

  // Convert to array and sort by YA
  return Object.values(yaGroups).sort((a, b) => a.ya - b.ya);
}

/**
 * Format date for display
 *
 * @param {Date} date - Date to format
 * @returns {string} Formatted date string
 */
export function formatDate(date) {
  return format(new Date(date), "dd MMM yyyy");
}
